import React, { useState } from "react";
import {
  Box, TextField, Button, Typography, Paper, Link,
  Dialog, DialogTitle, DialogContent, DialogActions
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import LockIcon from "@mui/icons-material/Lock";
import { useTranslation } from "react-i18next";
import MyAppHeader from "./MyAppHeader";
import api from "../services/api";
import { withCountryPrefix } from "./utils/routeHelpers";

const Login = () => {
  const { login } = useAuth();
  const { t } = useTranslation();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState(null);

  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetMessage, setResetMessage] = useState("");
  const [resetError, setResetError] = useState("");
  const [resetLoading, setResetLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const match = location.pathname.match(/^\/([a-z]{2})\b/);
  const country = match ? match[1] : "el";

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prevForm) => ({ ...prevForm, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      const response = await login(form.email, form.password);
      if (response.success && response.data) {
        const { role } = response.data;
        if (role === "admin") {
          navigate("/admin/dashboard");
        } else {
          navigate(withCountryPrefix("/client/dashboard", country), { replace: true });
        }
      } else {
        setError(response.message || t("login_failed"));
      }
    } catch (err) {
      setError(err.message || t("an_error_occurred"));
    }
  };

  const handleResetPassword = async () => {
    setResetMessage("");
    setResetError("");
    setResetLoading(true);
    try {
      const res = await api.users.requestPasswordReset(resetEmail);
      setResetMessage(res.data.message || t("reset_sent_message"));
      setResetLoading(false);
      setTimeout(() => {
        setResetDialogOpen(false);
        setResetEmail("");
        setResetMessage("");
        setResetError("");
      }, 2000);
    } catch (err) {
      setResetLoading(false);
      setResetError(err.response?.data?.error || t("something_went_wrong"));
    }
  };


  return (
    <>
      <MyAppHeader />
      <Box sx={{ minHeight: "80vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Paper elevation={4} sx={{ p: 4, maxWidth: 400, width: "100%", borderRadius: 3 }}>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <LockIcon color="primary" sx={{ fontSize: 48, mb: 1 }} />
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              {t("login_title") || "Login"}
            </Typography>
          </Box>
          {error && <Typography color="error" align="center" sx={{ mb: 2 }}>{error}</Typography>}
          <form onSubmit={handleSubmit}>
            <TextField
              label={t("email") || "Email"}
              name="email"
              type="email"
              margin="normal"
              fullWidth
              value={form.email}
              onChange={handleChange}
              required
              autoFocus
            />
            <TextField
              label={t("password") || "Password"}
              name="password"
              type="password"
              margin="normal"
              fullWidth
              value={form.password}
              onChange={handleChange}
              required
            />
            <Button variant="contained" color="primary" type="submit" fullWidth sx={{ mt: 2 }}>
              {t("login_btn") || "Login"}
            </Button>
          </form>
        </Paper>
        <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)}>
          <DialogTitle>{t("reset_password") || "Reset Password"}</DialogTitle>
          <DialogContent>
            <Typography sx={{ mb: 2 }}>
              {t("reset_instruction") || "Enter your email to receive reset instructions."}
            </Typography>
            <TextField
              label={t("email") || "Email"}
              type="email"
              value={resetEmail}
              onChange={(e) => setResetEmail(e.target.value)}
              fullWidth
              autoFocus
              disabled={resetLoading}
            />
            {resetMessage && (
              <Typography color="success.main" sx={{ mt: 2 }}>
                {resetMessage}
              </Typography>
            )}
            {resetError && (
              <Typography color="error" sx={{ mt: 2 }}>
                {resetError}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setResetDialogOpen(false)} disabled={resetLoading}>
              {t("cancel") || "Cancel"}
            </Button>
            <Button
              onClick={handleResetPassword}
              variant="contained"
              disabled={!resetEmail || resetLoading}
            >
              {resetLoading ? t("sending") || "Sending..." : t("send") || "Send"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
};

export default Login;
